<script lang="ts">
	import TooltipChip, { ChipTone } from '$lib/atoms/badges/TooltipChip.svelte'
	import IconParagraph from '$svg/icon-paragraph-outline.svg?component'
	import { TooltipPlacements } from '$types/enums'
	import { EquivalenceRole, type EquivalenceMark } from '$lib/analytics/utils/equivalence'
	import { t } from '$i18n/i18n'

	interface Props {
		/** parameter-specific footnote of the regulation, empty when the value has none */
		footnote?: string
		/**
		 * set when an equivalence footnote of the regulation moves this value out of, or into, the
		 * combined result
		 */
		equivalence?: EquivalenceMark
		placement?: TooltipPlacements
	}

	let { footnote = '', equivalence, placement = TooltipPlacements.RIGHT }: Props = $props()

	/** the § only fills three quarters of its own viewBox, hence the full 24px inside the 2rem chip */
	const GLYPH_SIZE = 24

	const isIgnored = $derived(equivalence?.role === EquivalenceRole.IGNORED)
	const isCounting = $derived(equivalence?.role === EquivalenceRole.COUNTING)
	// a value carrying neither role still shows its plain footnote, that is what the blue chip is for
	const isVisible = $derived(isIgnored || isCounting || footnote !== '')

	const tone = $derived.by(() => {
		if (isIgnored) return ChipTone.MUTED
		if (isCounting) return ChipTone.POSITIVE

		return ChipTone.INFO
	})

	const counterparts = $derived(equivalence?.counterparts.join(', ') ?? '')
	const analytics = $derived(equivalence?.analytics ?? [])

	const equivalenceReason = $derived(
		isIgnored
			? $t('Analytics.footnote.equivalence.ignored.tooltip', { counterparts })
			: $t('Analytics.footnote.equivalence.counting.tooltip', { counterparts })
	)
</script>

{#if isVisible}
	<TooltipChip
		name="footnote"
		{tone}
		title={equivalence ? $t('Analytics.footnote.equivalence.title') : ''}
		{placement}
	>
		{#snippet icon()}
			<span class="footnote-glyph">
				<IconParagraph width={GLYPH_SIZE} height={GLYPH_SIZE} />
			</span>
		{/snippet}
		{#if equivalence}
			<span>{@html equivalenceReason}</span>
			<!-- a lone analytic needs no naming, the row the chip sits in already belongs to it -->
			{#if analytics.length > 1}
				<span
					>{$t('Analytics.footnote.equivalence.analytics', {
						analytics: analytics.join(', ')
					})}</span
				>
			{/if}
			{#if footnote !== ''}
				<!-- the regulation's own wording, set apart from the effect explained above it -->
				<div class="footnote-source">
					<span>{$t('Analytics.footnote.equivalence.footnote')}</span>
					<span>{@html footnote}</span>
				</div>
			{/if}
		{:else}
			<span>{@html $t('UI.footnote.info') + footnote}</span>
		{/if}
	</TooltipChip>
{/if}

<style>
	.footnote-glyph {
		display: inline-flex;

		/**
		 * global.scss styles the bare `span` element, colour included, and a declaration on the
		 * element beats the tone colour the chip hands down by inheritance — so the glyph would
		 * paint black. This wrapper only exists to carry the rules below, so it must not recolour.
		 */
		color: inherit;

		/* the glyph is stroked, so its weight is set here rather than baked into the asset */
		& :global(svg) {
			stroke-width: 2.2;
		}
	}

	.footnote-source {
		display: flex;
		flex-direction: column;
		gap: 4px;

		/* adds to the tooltip body's own gap, so the quoted footnote reads as its own block */
		margin-top: 0.5rem;
	}
</style>
